<script lang="ts">
	import Magnifier from '$lib/icons/magnifier.svelte';
	import {
		type SbBlogArticleStory,
		type SbBlogExternalArticleStory,
		isExternalArticle
	} from '../types';
	import debounce from '../debounce';
	import FilledClose from '$lib/icons/filledClose.svelte';
	import { scale } from 'svelte/transition';
	import log from '$lib/utils/log';
	import { storyblok } from '$lib/state/storyblok.svelte';
	interface Props {
		onsearch: () => void;
		onresults: (results: Array<SbBlogArticleStory | SbBlogExternalArticleStory> | null) => void;
		grid?: boolean;
		searchTerm?: string;
	}

	let { onsearch, onresults, grid = true, searchTerm = $bindable('') }: Props = $props();

	async function searchStories(
		type: 'news' | 'blog',
		searchTerm: string
	): Promise<Array<SbBlogArticleStory | SbBlogExternalArticleStory>> {
		try {
			const { data } = await storyblok.client.getStories({
				by_slugs: `${type}/*`,
				resolve_relations: ['blog_page.category'],
				filter_query: {
					component: {
						in: 'blog_page,blog_external_article'
					}
				},
				search_term: searchTerm
			});

			return data.stories as Array<SbBlogArticleStory | SbBlogExternalArticleStory>;
		} catch (e) {
			log.exception(e);
			return [];
		}
	}

	async function getStories(searchTerm: string): Promise<void> {
		onsearch();

		const [news, blog] = await Promise.all([
			searchStories('news', searchTerm),
			searchStories('blog', searchTerm)
		]);

		const stories = [...news, ...blog].sort((a, b) => {
			const dateStrA = isExternalArticle(a)
				? a.first_published_at
				: a.content.custom_publish_date || a.first_published_at;

			const dateStrB = isExternalArticle(b)
				? b.first_published_at
				: b.content.custom_publish_date || b.first_published_at;

			if (!dateStrA || !dateStrB) {
				return 0;
			}

			const dateA = new Date(dateStrA);
			const dateB = new Date(dateStrB);

			return dateB.getTime() - dateA.getTime();
		});

		onresults(stories);
	}

	const debouncedSearch = debounce(() => {
		if (searchTerm.length === 0) {
			onresults(null);
			return;
		}

		getStories(searchTerm);
	}, 300);
</script>

<div
	class:col-span-full={grid}
	class="flex items-center gap-x-1 rounded-[60px] bg-stone-5 px-5 py-4 lg:gap-x-[6px] lg:rounded-[28px] lg:pl-5 lg:pr-4"
>
	<Magnifier class="h-6 w-6 text-brown-2" />
	<input
		bind:value={searchTerm}
		type="text"
		placeholder="Search topic"
		class="w-full bg-transparent text-[16px]/[140%] text-brown-1 outline-none placeholder:text-brown-2 lg:text-[18px]/[120%]"
		oninput={debouncedSearch}
	/>
	{#if searchTerm.length > 0}
		<button
			transition:scale
			type="button"
			onclick={(e) => {
				e.stopPropagation();
				onresults(null);
				searchTerm = '';
			}}
		>
			<FilledClose class="h-6 w-6 text-brown-1 max-lg:hidden" />
		</button>
	{/if}
</div>
